import { useQuery } from '@tanstack/react-query';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { partnerDashboardApi } from '../../api/partner-dashboard-reports';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';

const RISK_COLORS: Record<string, string> = { low: '#15803d', moderate: '#b45309', high: '#c2410c', critical: '#b91c1c', unrated: '#9ca3af' };

/** Partner Due Diligence Dashboard (spec section 49) - real aggregation over live data, no fabricated numbers. */
export function PartnerDueDiligenceDashboardPage() {
  const kpisQuery = useQuery({ queryKey: ['pdd-kpis'], queryFn: () => partnerDashboardApi.getKpis() });
  const byRiskQuery = useQuery({ queryKey: ['pdd-by-risk'], queryFn: () => partnerDashboardApi.getByRisk() });
  const renewalsQuery = useQuery({ queryKey: ['pdd-renewals'], queryFn: () => partnerDashboardApi.getUpcomingRenewals(90) });
  const actionsQuery = useQuery({ queryKey: ['pdd-actions'], queryFn: () => partnerDashboardApi.getOutstandingActions() });
  const missingDocsQuery = useQuery({ queryKey: ['pdd-missing-docs'], queryFn: () => partnerDashboardApi.getMissingDocuments() });

  const kpis = kpisQuery.data?.data;
  const riskData = (byRiskQuery.data?.data ?? []).filter((r) => r.count > 0);

  return (
    <div className="page">
      <PageHeader title="Partner Due Diligence Dashboard" description="Live status across the partner portfolio." />

      {kpis && (
        <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          {[
            ['Total Partners', kpis.total_partners], ['Active', kpis.active_count], ['Pending', kpis.pending_count],
            ['Approved', kpis.approved_count], ['Conditional', kpis.conditional_count], ['High Risk', kpis.high_risk_count],
            ['Critical', kpis.critical_count], ['Expiring in 30 Days', kpis.expiring_30_days], ['Expiring in 90 Days', kpis.expiring_90_days],
          ].map(([label, value]) => (
            <div key={label as string} className="card">
              <p className="muted">{label}</p>
              <p style={{ fontSize: '1.8em', margin: 0 }}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="panel">
        <SectionHeader title="Partners by Risk" />
        <div className="panel-body">
          {riskData.length > 0 && (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={riskData} dataKey="count" nameKey="risk_rating" cx="50%" cy="50%" innerRadius={65} outerRadius={95} paddingAngle={2}>
                  {riskData.map((entry) => <Cell key={entry.risk_rating} fill={RISK_COLORS[entry.risk_rating] ?? '#9ca3af'} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="panel">
        <SectionHeader title="Renewals Due (next 90 days)" />
        <div className="panel-body">
          {renewalsQuery.data && (
            <DataTable
              rows={renewalsQuery.data.data}
              rowKey={(r) => r.id}
              emptyMessage="No renewals due in the next 90 days."
              columns={[{ header: 'Partner', render: (r) => r.legal_name }, { header: 'Reference', render: (r) => r.partner_reference }, { header: 'Next Review', render: (r) => r.next_review_date }]}
            />
          )}
        </div>
      </div>

      <div className="panel">
        <SectionHeader title="Outstanding Corrective Actions" />
        <div className="panel-body">
          {actionsQuery.data && (
            <DataTable
              rows={actionsQuery.data.data}
              rowKey={(a) => a.id}
              emptyMessage="No outstanding corrective actions."
              columns={[{ header: 'Partner', render: (a) => a.legal_name }, { header: 'Finding', render: (a) => a.finding }, { header: 'Due', render: (a) => a.due_date ?? '-' }, { header: 'Status', render: (a) => a.status }]}
            />
          )}
        </div>
      </div>

      <div className="panel">
        <SectionHeader title="Missing Documents" />
        <div className="panel-body">
          {missingDocsQuery.data && (
            <DataTable
              rows={missingDocsQuery.data.data}
              rowKey={(d) => d.id}
              emptyMessage="No approved partners are missing verified documents."
              columns={[{ header: 'Partner', render: (d) => d.legal_name }, { header: 'Reference', render: (d) => d.partner_reference }]}
            />
          )}
        </div>
      </div>
    </div>
  );
}
